import React, { useState } from "react";

// Components
import { OrderDetails, Notes } from "@secondcloset/web-components";
import EditShippingInfo from "./EditShippingInfo";
import { Button, message, Select } from "antd";

// Types
import { Common, Fulfillment } from "@secondcloset/types";
import { UploadFile } from "antd/lib/upload/interface";
import { includes, some, startCase } from "lodash";
import { SelectSalesChannel } from "../../../SelectSalesChannel";
import { useMutation } from "react-query";
import { updateOrder } from "api/fulfillment/order";

const SERVICE_CODES = [
  { value: "second_closet_standard", label: "Threshold" },
  { value: "second_closet_white_glove", label: "White Glove" },
  { value: "second_closet_to_the_door", label: "To the Door" },
  { value: "second_closet_room_of_choice", label: "Room of Choice" },
];

type File = Common.File;
type Customer = Fulfillment.Customer;
type CustomerAddress = Fulfillment.CustomerAddress;

interface Props {
  orderID: string;
  orderPlatform?: Fulfillment.Order["platform"];
  shipmentTags?: Fulfillment.Order["shipment_tags"];
  address: CustomerAddress;
  customer: Customer;
  externalOrderNumber: string;
  shippingService: string;
  serviceCode: string;
  internalNotes: string;
  marketPlaceNotes: string;
  marketplaceNotesTitle: string;
  fulfilledFrom: string;
  signatureRequired?: boolean;
  isLoading: boolean;
  insuranceValue: number;
  organizationID: string;
  createdDate?: string;
  files?: File[];
  hasBoltShippingMethod?: boolean;
  onUpdateOrder: (payload: Record<string, unknown>) => void;
  onFileUpload: (file: UploadFile) => unknown;
  onFileRemove: (file: File) => unknown;
  onFileDownload: (file: File, fileName: string) => unknown;
  onUpdateSuccess: () => any;
  salesChannel: Fulfillment.Order["sales_channel"];
  refetchOrder: () => unknown;
}

const DetailsAndNotes: React.FC<Props> = ({
  orderID,
  orderPlatform,
  address,
  customer,
  shipmentTags = [],
  externalOrderNumber,
  shippingService,
  serviceCode,
  internalNotes,
  marketPlaceNotes,
  marketplaceNotesTitle,
  fulfilledFrom,
  signatureRequired,
  isLoading,
  insuranceValue,
  createdDate,
  organizationID,
  files,
  hasBoltShippingMethod,
  onUpdateOrder,
  onFileUpload,
  onFileRemove,
  onFileDownload,
  onUpdateSuccess,
  salesChannel,
  refetchOrder,
}) => {
  const [isEditShippingInfoModalVisible, setIsEditShippingInfoModalVisible] = useState(false);

  const name = address?.contact_name || customer?.name || "-";
  const email = customer?.email_address || "-";
  const phone = address?.phone_number || customer?.phone_number || "-";
  const customerPhoneNumber = customer?.phone_number || "-";
  const eveningPhoneNumber = address?.evening_phone_number || "-";
  const cellPhoneNumber = address?.cell_phone_number || "-";
  const isOrderOpen = some(shipmentTags, (tags) => includes(["ready_to_fulfill", "unfulfilled_items"], tags));
  const isOrderFulfilled = shipmentTags.every((tag) => tag === "items_fulfilled");

  const { mutate: doUpdateOrder } = useMutation(updateOrder, {
    onSuccess: () => {
      message.success(`Order updated successfully.`);
      refetchOrder();
    },
  });

  const handleUpdateServiceCode = (serviceCode?: string) => {
    return doUpdateOrder({
      orderID,
      body: {
        orders: {
          metadata_attributes: [
            {
              name: "service_code",
              value: serviceCode || "-",
              ...(!serviceCode && { _destroy: true }),
            },
          ],
        },
      },
    });
  };

  const renderServiceCode = () => {
    const code = serviceCode || "-";
    if (!isOrderOpen) return startCase(code.replace("second_closet", ""));
    return (
      <Select
        style={{ width: "100%" }}
        dropdownStyle={{ minWidth: 200 }}
        bordered={false}
        loading={isLoading}
        value={code}
        allowClear
        onChange={(s) => handleUpdateServiceCode(s)}
      >
        {SERVICE_CODES.map((f) => (
          <Select.Option key={f.value} label={f.label} value={f.value}>
            {f.label}
          </Select.Option>
        ))}
      </Select>
    );
  };

  const renderEditShippingInfoBtn = () => {
    return (
      <Button style={{ margin: "1em 0" }} onClick={() => setIsEditShippingInfoModalVisible(true)} type="primary" ghost>
        Edit Shipping Information
      </Button>
    );
  };

  const renderSalesChannel = () => {
    if (isOrderFulfilled || isOrderOpen) {
      return (
        <SelectSalesChannel
          bordered={false}
          value={salesChannel}
          orgId={organizationID}
          fallbackOptions={isOrderFulfilled ? [{ value: "wholesale", label: "Wholesale" }] : []}
          onChange={(channel) => onUpdateOrder({ sales_channel: channel || null })}
          allowClear={!!salesChannel}
        />
      );
    }

    return <>{startCase(salesChannel) || "-"}</>;
  };

  return (
    <div className="details-and-notes">
      <div className="details">
        <OrderDetails
          address={address}
          name={name}
          email={email}
          phone={phone}
          customerPhoneNumber={customerPhoneNumber}
          eveningPhoneNumber={eveningPhoneNumber}
          cellPhoneNumber={cellPhoneNumber}
          externalOrderNumber={externalOrderNumber}
          shippingService={shippingService}
          serviceCode={renderServiceCode()}
          fulfilledFrom={fulfilledFrom}
          signatureRequired={signatureRequired}
          insuranceValue={insuranceValue}
          createdDate={createdDate}
          renderEditShippingInfoBtn={renderEditShippingInfoBtn}
          hasBoltShippingMethod={hasBoltShippingMethod}
          salesChannel={renderSalesChannel()}
        />

        {orderID && orderPlatform ? (
          <EditShippingInfo
            orderID={orderID}
            orgID={organizationID}
            customer={customer}
            address={address}
            orderPlatform={orderPlatform}
            isChangeCustomerDisabled={!isOrderOpen}
            isModalVisible={isEditShippingInfoModalVisible}
            onIsModalVisibleChange={setIsEditShippingInfoModalVisible}
            onUpdateSuccessCallback={onUpdateSuccess}
          />
        ) : null}
      </div>

      <div className="order-notes">
        <Notes
          internalNotes={internalNotes}
          marketplaceNotes={marketPlaceNotes}
          notesTitle="Internal Notes"
          marketplaceNotesTitle={marketplaceNotesTitle}
          isUploadDisabled={false}
          noteLoading={isLoading}
          showMarketPlaceNotes={true}
          size="large"
          onUpdateNotes={(notes) => onUpdateOrder({ internal_notes: notes })}
          files={files}
          onFileUpload={onFileUpload}
          onFileRemove={onFileRemove}
          onFileDownload={onFileDownload}
        />
      </div>
    </div>
  );
};

export default DetailsAndNotes;
